import { KeyboardShortcutState, State, KeyboardShortcutDefinition } from './types';
import {
	SHORTCUT_MANAGER_SHORTCUTS,
	SHORTCUT_PANEL_SHORTCUT,
	SHORTCUT_SPOTLIGHT_SHORTCUT,
	SHORTCUT_VOICE_SHORTCUT,
} from '../shortcuts';

type Options = {
	includeSpotlight: boolean;
	includePanel: boolean;
};

function compareSets<T>(a: Set<T>, b: Set<T>) {
	if (a.size !== b.size) return false;
	for (var val of a) if (!b.has(val)) return false;
	return true;
}

export function initializeShortcut(definition: KeyboardShortcutDefinition): KeyboardShortcutState {
	return {
		...definition,
		keys: definition.initialKeys,
		isActive: true,
		isInPage: false,
		instances: 0,
		triggerIfUnfocusedSingleInstance: false,
		hasConflict: false,
	};
}

export function initializeStateFromDefinition(
	allShortcuts: KeyboardShortcutDefinition[],
	options: Options = {
		includeSpotlight: true,
		includePanel: true,
	},
): State {
	let previousState: null | State = null;
	try {
		const localData = localStorage.getItem('EPO_KEYBOARD_SHORTCUTS_STATE');
		if (localData) {
			previousState = JSON.parse(localData);
		}
	} catch (error) {}

	let newShortcutConflictingIds: string[] = [];
	if (previousState !== null) {
		// go to all shortcuts of new release
		// if the shortcut is new
		// do we have a conflict with a user overriden shortcut?
		const newShortcuts = allShortcuts.filter(shortcut => {
			return !previousState?.shortcuts[shortcut.id];
		});
		const previousShortcutKeysSets: Set<string>[] = Object.keys(previousState?.shortcuts)
			.map(key => {
				return new Set(previousState?.shortcuts[key]?.keys.split('+'));
			})
			.filter(Boolean);
		newShortcutConflictingIds = newShortcuts
			.filter(newShortcut => {
				const curSet: Set<string> = new Set(newShortcut.initialKeys.split('+'));
				const foundAConflict = previousShortcutKeysSets.find(set => compareSets(set, curSet));

				return foundAConflict;
			})
			.map(newShortcut => newShortcut.id);
	}

	const allShortcutsState: Record<string, KeyboardShortcutState> = ([] as KeyboardShortcutDefinition[])
		.concat(options.includePanel ? [SHORTCUT_PANEL_SHORTCUT] : [])
		.concat(options.includeSpotlight ? [SHORTCUT_SPOTLIGHT_SHORTCUT, SHORTCUT_VOICE_SHORTCUT] : [])
		.concat(allShortcuts)
		.reduce((acc, item) => {
			const keys = (previousState as State)?.shortcuts[item.id]?.keys ?? item.initialKeys;
			const hasConflict =
				(previousState as State)?.shortcuts[item.id]?.hasConflict ||
				newShortcutConflictingIds.includes(item.id);
			return {
				...acc,
				[item.id]: {
					...item,
					keys,
					isActive: hasConflict
						? // if there is a conflict with this new shortcut, disable it.
						  false
						: (previousState as State)?.shortcuts[item.id]?.isActive ?? true,
					isInPage: false,
					instances: 0,
					hasConflict,
				},
			};
		}, {});

	const initialState: State = {
		shortcuts: {
			...allShortcutsState,
		},
	};

	return initialState;
}
